import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "tab",
    "pickedInstructorToken",
    "radio",
    "programId",
    "startTime",
    "availableToTeens",
    "availableToAdults",
    "teensCanScheduleOnline",
    "adultsCanScheduleOnline",
    "availableStudentProgram",
    "descriptionSummary",
    "descriptionAgenda",
    "descriptionPrivateNotes",
    "daysSessionsRun",
    "maxInPersonAttendees",
    "maxInPersonMakeUp",
    "maxRemoteAttendees",
    "maxRemoteMakeUp",
    "remoteLink",
    "validationBlock",
    "validationMessage",
    "saveButton",
    "confirmPanel",
    "confirmMessage",
    "studentProgramSection",
    "studentProgramRadio",
    "studentProgramOption",
    "programOptions",
    "enterCodeContainer",
    "codeField",
    "introductoryMeetingsSection",
    "introductoryMeetingsForProgram",
    "introductoryMeetingRadio",
    "introductoryMeetingAttendanceFields"
  ];

  static values = {
    minRows: { type: Number, default: 1 },
    paramName: String
  }

  connect() {
    this._courseworkRemoveRequest = null;
    this._initialCourseworkById = this.initialCourseworkById();

    if (this.hasIntroductoryMeetingsSectionTarget && this.hasRadioTarget) {
      const firstRadio = this.radioTargets.find(radio => radio.checked);
      if (firstRadio) {
        const event = { target: firstRadio };
        this.updateIntroductoryMeetingsSection(event);
      }
    }
    
    if (this.hasStudentProgramSectionTarget && this.hasRadioTarget) {
      const firstRadio = this.radioTargets.find(radio => radio.checked);
      if (firstRadio) {
        const event = { target: firstRadio };
        this.updateStudentProgramSection(event);
      }
    }

    this._onClick = (event) => {
      // Did we click a coursework remove button?
      const removeBtn = event.target.closest('button[data-action*="add-to-form#remove"]');
      if (!removeBtn) return;

      const addToFormContainer = removeBtn.closest('[data-controller~="add-to-form"]');
      if (!addToFormContainer) return;

      const addToFormRow = removeBtn.closest('[data-add-to-form-target="row"]');
      if (!addToFormRow) return;

      const addToFormController = this.application.getControllerForElementAndIdentifier(addToFormContainer, "add-to-form");
      if (addToFormController && addToFormController.rowTargets.length <= addToFormController.minRowsValue) return;

      // Stop the add-to-form controller from handling the click
      event.preventDefault();
      event.stopImmediatePropagation();

      this.hideValidationBlock();

      const message = "Removing a coursework item will remove all of its associated scores. Continue?";

      this.showConfirmPanelForRow(addToFormRow, addToFormController, message);
    }

    this.element.addEventListener("click", this._onClick, true);
  }

  disconnect() {
    if (this._onClick) this.element.removeEventListener("click", this._onClick, true);
  }

  initialCourseworkById() {
    const map = new Map();

    const addToFormContainer = this.element.querySelector('[data-controller~="add-to-form"]');
    if (!addToFormContainer) return map;

    const rows = addToFormContainer.querySelectorAll('[data-add-to-form-target="container"] [data-add-to-form-target="row"]');

    rows.forEach((row) => {
      const id = (row.querySelector('input[name$="[id]"]')?.value || "").trim();
      if (!id) return;

      const name = (row.querySelector('input[name$="[name]"]')?.value || "").trim();
      const weight = (row.querySelector('input[name$="[weight]"]')?.value || "").trim();

      map.set(id, { name, weight });
    });

    return map;
  }

  submit(event) {
    event.preventDefault();
    const selectedProgram = this.getSelectedProgram();
    const pupilId = this.getPupilId();
    
    if (selectedProgram && pupilId) {
      this.postForm(selectedProgram, pupilId);
    } else {
        console.error('No program selected');
    }
  }

  getSelectedProgram() {
      const selectedRadio = this.radioTargets.find(radio => radio.checked);
      return selectedRadio ? selectedRadio.value : null;
  }

  getPupilId() {
    const hiddenField = document.getElementById("pupil_id");
    if (hiddenField) {
      return hiddenField.value;
    }
    
    const selectedPupilRadio = document.querySelector('input[name="selected-item-pupil"]:checked');
    if (selectedPupilRadio) {
      return selectedPupilRadio.value;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('picked_pupil') || urlParams.get('pupil_id');
  }

  updateStudentProgramSection(event) {
    if (!this.hasStudentProgramSectionTarget) return;
    
    const selectedProgramId = event.target.value;
    const section = this.studentProgramSectionTarget;
    
    const programsMap = JSON.parse(section.dataset.studentProgramsMap || '{}');
    const availableStudentProgramIds = programsMap[selectedProgramId] || [];
    
    if (availableStudentProgramIds.length > 0) {
      section.classList.remove('u-hidden');
      
      const options = section.querySelectorAll('[data-student-program-id]');
      options.forEach(option => {
        const programId = parseInt(option.dataset.studentProgramId);
        if (availableStudentProgramIds.includes(programId)) {
          option.classList.remove('u-hidden');
        } else {
          option.classList.add('u-hidden');
          const radio = option.querySelector('input[type="radio"]');
          if (radio && radio.checked) {
            const noneRadio = section.querySelector('#student_program_none');
            if (noneRadio) noneRadio.checked = true;
          }
        }
      });
      
      this.programOptionsTargets.forEach(options => {
        options.hidden = true;
        const checkbox = options.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = false;
        const codeField = options.querySelector('[data-driving-school-classroom-quick-look-target="codeField"]');
        if (codeField) codeField.value = '';
        const codeContainer = options.querySelector('[data-driving-school-classroom-quick-look-target="enterCodeContainer"]');
        if (codeContainer) codeContainer.hidden = true;
      });
      
      const checkedRadio = section.querySelector('input[name="selected_student_program"]:checked');
      if (checkedRadio && checkedRadio.value) {
        this.updateStudentProgramOptions({ target: checkedRadio });
      }
    } else {
      section.classList.add('u-hidden');
      const noneRadio = section.querySelector('#student_program_none');
      if (noneRadio) noneRadio.checked = true;
    }
  }

  updateStudentProgramOptions(event) {
    if (!this.hasStudentProgramSectionTarget) return;
    
    const section = this.studentProgramSectionTarget;
    const selectedStudentProgramId = event.target.value;
    
    this.programOptionsTargets.forEach(options => {
      options.hidden = true;
      const checkbox = options.querySelector('input[type="checkbox"][name="has_financial_scholarship"]');
      if (checkbox) checkbox.checked = false;
      const codeField = options.querySelector('[data-driving-school-classroom-quick-look-target="codeField"]');
      if (codeField) codeField.value = '';
      const codeContainer = options.querySelector('[data-driving-school-classroom-quick-look-target="enterCodeContainer"]');
      if (codeContainer) codeContainer.hidden = true;
    });
    
    if (!selectedStudentProgramId) return;
    
    const programOptions = Array.from(this.programOptionsTargets).find(options => {
      return options.dataset.studentProgramId === selectedStudentProgramId;
    });
    
    if (programOptions) {
      programOptions.hidden = false;
    }
  }

  toggleCodeField(event) {
    const financialScholarshipCheckbox = event.target;
    if (financialScholarshipCheckbox.type !== 'checkbox') return;
    
    const programOptions = financialScholarshipCheckbox.closest('[data-driving-school-classroom-quick-look-target="programOptions"]');
    if (!programOptions) return;
    
    const enterCodeContainer = programOptions.querySelector('[data-driving-school-classroom-quick-look-target="enterCodeContainer"]');
    const codeField = programOptions.querySelector('[data-driving-school-classroom-quick-look-target="codeField"]');
    
    if (!enterCodeContainer) return;
    
    const checked = financialScholarshipCheckbox.checked;
    enterCodeContainer.hidden = !checked;
    
    if (!checked && codeField) {
      codeField.value = '';
    }
  }

  validateCodeField() {
    if (!this.hasStudentProgramSectionTarget) return null;
    
    const selectedStudentProgram = this.getSelectedStudentProgram();
    if (!selectedStudentProgram) return null;
    
    const programOptions = Array.from(this.programOptionsTargets).find(options => {
      return options.dataset.studentProgramId === selectedStudentProgram && !options.hidden;
    });
    
    if (!programOptions) return null;
    
    const financialScholarshipCheckbox = programOptions.querySelector('input[type="checkbox"][name="has_financial_scholarship"]');
    if (!financialScholarshipCheckbox || !financialScholarshipCheckbox.checked) return null;
    
    const codeField = programOptions.querySelector('[data-driving-school-classroom-quick-look-target="codeField"]');
    if (!codeField) return null;
    
    const code = codeField.value.trim();
    if (code === '') return null;
    
    const requiredLength = parseInt(codeField.dataset.codeLength || '0');
    
    if (requiredLength > 0 && code.length !== requiredLength) {
      return `Your code must be ${requiredLength} characters long.`;
    }
    
    return null;
  }

  getSelectedIntroductoryMeetingSessionId() {
    if (!this.hasIntroductoryMeetingsSectionTarget || !this.hasIntroductoryMeetingRadioTarget) return null;
    const section = this.introductoryMeetingsSectionTarget;
    if (section.classList.contains('u-hidden')) return null;
    const selectedRadio = this.introductoryMeetingRadioTargets.find(radio => radio.checked && !radio.closest('.u-hidden'));
    return selectedRadio ? selectedRadio.value : null;
  }

  getSelectedStudentProgram() {
    if (!this.hasStudentProgramSectionTarget || !this.hasStudentProgramRadioTarget) return null;
    const section = this.studentProgramSectionTarget;
    if (section.classList.contains('u-hidden')) return null;
    const selectedRadio = this.studentProgramRadioTargets.find(radio => radio.checked && !radio.closest('.u-hidden'));
    return selectedRadio ? selectedRadio.value : null;
  }

  validateIntroductoryMeeting() {
    const selectedRadio = this.introductoryMeetingRadioTargets.find(radio => radio.checked);
    if (!selectedRadio) return null;
    
    const sessionId = selectedRadio.dataset.sessionId;
    const allowsInPerson = selectedRadio.dataset.allowsInPerson === 'true';
    const allowsRemote = selectedRadio.dataset.allowsRemote === 'true';
    const maxInPerson = parseInt(selectedRadio.dataset.maxInPerson || '0', 10);
    const maxRemote = parseInt(selectedRadio.dataset.maxRemote || '0', 10);
    const currentInPerson = parseInt(selectedRadio.dataset.currentInPerson || '0', 10);
    const currentRemote = parseInt(selectedRadio.dataset.currentRemote || '0', 10);
    
    const attendanceTypeInput = document.querySelector(`input[name="introductory_meeting_attendance_type_${sessionId}"]:checked`);
    if (!attendanceTypeInput) {
      return "Please select how you will attend the introductory meeting (in-person or remote).";
    }
    
    const attendanceType = attendanceTypeInput.value;
    
    if (attendanceType === 'in_person' && !allowsInPerson) {
      return "In-person attendance is not available for the selected introductory meeting.";
    }
    
    if (attendanceType === 'remote' && !allowsRemote) {
      return "Remote attendance is not available for the selected introductory meeting.";
    }
    
    const numberOfAttendeesInput = document.querySelector(`#introductory_meeting_number_of_attendees_${sessionId}`);
    if (!numberOfAttendeesInput || !numberOfAttendeesInput.value || parseInt(numberOfAttendeesInput.value, 10) < 1) {
      return "Please set number of attendees for the selected introductory meeting.";
    }
    
    const numberOfAttendees = parseInt(numberOfAttendeesInput.value, 10);
    
    if (attendanceType === 'in_person' && maxInPerson > 0) {
      const projectedTotal = currentInPerson + numberOfAttendees;
      if (projectedTotal > maxInPerson) {
        return `Adding ${numberOfAttendees} in-person ${numberOfAttendees === 1 ? 'attendee' : 'attendees'} would exceed the maximum of ${maxInPerson} in-person attendees for the selected introductory meeting (currently ${currentInPerson} expected, would be ${projectedTotal}).`;
      }
    }
    
    if (attendanceType === 'remote' && maxRemote > 0) {
      const projectedTotal = currentRemote + numberOfAttendees;
      if (projectedTotal > maxRemote) {
        return `Adding ${numberOfAttendees} remote ${numberOfAttendees === 1 ? 'attendee' : 'attendees'} would exceed the maximum of ${maxRemote} remote attendees for the selected introductory meeting (currently ${currentRemote} expected, would be ${projectedTotal}).`;
      }
    }
    
    return null;
  }

  updateIntroductoryMeetingsSection(event) {
    if (!this.hasIntroductoryMeetingsSectionTarget) return;
    
    const selectedProgramId = event.target.value;
    const section = this.introductoryMeetingsSectionTarget;
    
    this.introductoryMeetingsForProgramTargets.forEach(programSection => {
      programSection.classList.add('u-hidden');
    });
    
    this.introductoryMeetingRadioTargets.forEach(radio => {
      radio.checked = false;
      this.hideIntroductoryMeetingFieldsForSession(radio.dataset.sessionId);
    });
    
    const selectedProgramSection = this.introductoryMeetingsForProgramTargets.find(
      programSection => programSection.dataset.programId === selectedProgramId
    );
    
    if (selectedProgramSection) {
      section.classList.remove('u-hidden');
      selectedProgramSection.classList.remove('u-hidden');
    } else {
      section.classList.add('u-hidden');
    }
  }

  toggleIntroductoryMeetingFields(event) {
    const radio = event.target;
    const sessionId = radio.dataset.sessionId;
    
    this.introductoryMeetingRadioTargets.forEach(r => {
      if (r.dataset.sessionId !== sessionId) {
        this.hideIntroductoryMeetingFieldsForSession(r.dataset.sessionId);
      }
    });
    
    if (radio.checked) {
      this.showIntroductoryMeetingFieldsForSession(sessionId);
    } else {
      this.hideIntroductoryMeetingFieldsForSession(sessionId);
    }
  }

  showIntroductoryMeetingFieldsForSession(sessionId) {
    if (!this.hasIntroductoryMeetingAttendanceFieldsTarget) return;
    
    const fields = this.introductoryMeetingAttendanceFieldsTargets.find(
      field => field.dataset.sessionId === sessionId
    );
    
    if (fields) {
      fields.classList.remove('u-hidden');
      fields.hidden = false;
    }
  }

  hideIntroductoryMeetingFieldsForSession(sessionId) {
    if (!this.hasIntroductoryMeetingAttendanceFieldsTarget) return;
    
    const fields = this.introductoryMeetingAttendanceFieldsTargets.find(
      field => field.dataset.sessionId === sessionId
    );
    
    if (fields) {
      fields.classList.add('u-hidden');
      fields.hidden = true;
      
      const inPersonInput = fields.querySelector(`input[name="introductory_meeting_attendance_type_${sessionId}"][value="in_person"]`);
      const remoteInput = fields.querySelector(`input[name="introductory_meeting_attendance_type_${sessionId}"][value="remote"]`);
      const numberOfAttendeesInput = fields.querySelector(`#introductory_meeting_number_of_attendees_${sessionId}`);
      
      if (inPersonInput) inPersonInput.checked = true;
      if (remoteInput) remoteInput.checked = false;
      if (numberOfAttendeesInput) numberOfAttendeesInput.value = '1';
    }
  }

  async postForm(selectedProgram, pupilId) {
    const introductoryMeetingValidationError = this.validateIntroductoryMeeting();
    if (introductoryMeetingValidationError) {
      this.toggleErrorPanel(true, introductoryMeetingValidationError);
      return;
    }

    const selectedStudentProgram = this.getSelectedStudentProgram();
    
    const codeValidationError = this.validateCodeField();
    if (codeValidationError) {
      this.toggleErrorPanel(true, codeValidationError);
      return;
    }
    
    try {
      const bodyParams = new URLSearchParams({ 
        selected_program: selectedProgram,
        pupil_id: pupilId
      });

      const selectedIntroductoryMeetingSessionId = this.getSelectedIntroductoryMeetingSessionId();

      if (selectedIntroductoryMeetingSessionId) {
        const sessionId = selectedIntroductoryMeetingSessionId;
        bodyParams.append('selected_introductory_meeting_session', sessionId);
        
        const attendanceTypeInput = document.querySelector(`input[name="introductory_meeting_attendance_type_${sessionId}"]:checked`);
        if (attendanceTypeInput) {
          bodyParams.append('introductory_meeting_attendance_type', attendanceTypeInput.value);
        }
        
        const numberOfAttendeesInput = document.querySelector(`#introductory_meeting_number_of_attendees_${sessionId}`);
        if (numberOfAttendeesInput) {
          bodyParams.append('introductory_meeting_number_of_attendees', numberOfAttendeesInput.value);
        }
      }
      
      if (selectedStudentProgram) {
        bodyParams.append('selected_student_program', selectedStudentProgram);
        
        const programOptions = Array.from(this.programOptionsTargets).find(options => {
          return options.dataset.studentProgramId === selectedStudentProgram && !options.hidden;
        });
        
        if (programOptions) {
          const financialScholarshipCheckbox = programOptions.querySelector('input[type="checkbox"][name="has_financial_scholarship"]');
          if (financialScholarshipCheckbox && financialScholarshipCheckbox.checked) {
            bodyParams.append('has_financial_scholarship', '1');
          }
          
          const codeField = programOptions.querySelector('input[name="code"]');
          if (codeField && codeField.value) {
            bodyParams.append('code', codeField.value);
          }
        }
      }
      
      const response = await fetch("/schools/classroom/program/quick-look/schedule", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: bodyParams
      });

      if (response.ok) {
        const stream = await response.text();
        Turbo.renderStreamMessage(stream);
        this.toggleErrorPanel(false);
      } else {
        const responseBody = await response.json();
  
        if (responseBody.error) {
          const errorText = responseBody.error;
          this.toggleErrorPanel(true, errorText);
        } else {
          console.error("Unexpected error: ", responseBody);
        }
      }
    } catch (error) {
      console.error("Error fetching form:", error);
    }
  }

  deleteProgram(event) {
    event.preventDefault();

    // find this button and disable it
    const deleteButton = event.target;
    deleteButton.hidden = true;

    const programId = event.target.dataset.drivingSchoolClassroomQuickLookProgramId;

    if (programId) {
      this.postDeleteForm(programId);
    } else {
      console.error(programId);
      console.error('programId  does not exist');
    }
  }


  updateProgram(event) {
    event.preventDefault();

    const errors = this.validateForm();

    if (errors.length > 0) {
      this.showValidationBlock(errors.join("<br>"));
      return;
    }

    const programId = event.target.dataset.drivingSchoolClassroomQuickLookProgramId;
    
    if (programId) {
      this.postEditForm(programId);
    } else {
        console.error('programId does not exist');
    }
  }
  
  async postEditForm(programId) {
    try {
      const formData = {
        driving_school_classroom_program: {
          start_time: this.startTimeTarget.value,
          available_to_teens: this.availableToTeensTarget.checked,
          available_to_adults: this.availableToAdultsTarget.checked,
          teens_can_schedule_online: this.teensCanScheduleOnlineTarget.checked,
          adults_can_schedule_online: this.adultsCanScheduleOnlineTarget.checked,
          description_summary_english: this.descriptionSummaryTarget.value,
          description_agenda_english: this.descriptionAgendaTarget.value,
          description_private_notes_english: this.descriptionPrivateNotesTarget.value,
          days_sessions_run: this.daysSessionsRunTargets
            .filter(target => target.checked)
            .map(target => target.value),
          coursework: this.getCourseworkValues()
        }
      };

      // Add capacity fields if they exist
      if (this.hasMaxInPersonAttendeesTarget) {
        formData.driving_school_classroom_program.max_number_of_in_person_attendees = 
          this.maxInPersonAttendeesTarget.value;
      }

      if (this.hasMaxInPersonMakeUpTarget) {
        formData.driving_school_classroom_program.max_number_of_in_person_make_up = 
          this.maxInPersonMakeUpTarget.value;
      }

      if (this.hasMaxRemoteAttendeesTarget) {
        formData.driving_school_classroom_program.max_number_of_remote_attendees = 
          this.maxRemoteAttendeesTarget.value;
      }

      if (this.hasMaxRemoteMakeUpTarget) {
        formData.driving_school_classroom_program.max_number_of_remote_make_up = 
          this.maxRemoteMakeUpTarget.value;
      }

      if (this.hasRemoteLinkTarget) {
        formData.driving_school_classroom_program.remote_link = 
          this.remoteLinkTarget.value;
      }

      if (this.hasAvailableStudentProgramTarget) {
        formData.driving_school_classroom_program.available_student_program_ids = 
          this.availableStudentProgramTargets
            .filter(target => target.checked)
            .map(target => target.value);
      }

      const response = await fetch(`/schools/classroom/program/${programId}/quick-look/edit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const stream = await response.text();
        Turbo.renderStreamMessage(stream);
      } else {
        const responseBody = await response.json();
  
        if (responseBody.error) {
          const errorText = responseBody.error;
          console.log(errorText);
          this.showValidationBlock(errorText);
        } else {
          console.error("Unexpected error: ", responseBody);
        }
      }
    } catch (error) {
      console.error("Error fetching form:", error);
    }
  }

  async postDeleteForm(programId) {
    try {
      const response = await fetch(`/schools/classroom/program/${programId}/quick-look/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      });

      if (response.ok) {
        const stream = await response.text();
        Turbo.renderStreamMessage(stream);
        // wait 2 seconds and reload the page
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        alert("Error deleting program, please reload the page and try again.");
      }
    } catch (error) {
      alert("Error deleting program, please reload the page and try again.");
      console.error("Error fetching form:", error);
    }
  }

  getCourseworkValues() {
    const addToFormContainers = this.element.querySelectorAll('[data-controller~="add-to-form"]');

    const addToFormContainer = addToFormContainers[0];
    if (!addToFormContainer) return [];

    const addToFormRows = addToFormContainer.querySelectorAll(
      '[data-add-to-form-target="container"] [data-add-to-form-target="row"]'
    );

    return Array.from(addToFormRows)
      .map((row) => {
        const id = (row.querySelector('input[name$="[id]"]')?.value || "").trim();
        const name = (row.querySelector('input[name$="[name]"]')?.value || "").trim();
        const weight = (row.querySelector('input[name$="[weight]"]')?.value || "").trim();
        return { id, name, weight };
      })
      .filter(i => i.name !== "" || i.weight !== "");
  }

  closeQuickLook(event) {
    event.preventDefault();
    const quickViewElement = document.querySelector("#school-classrooms-quick-look");
    quickViewElement.classList.add("u-hidden");
  }

  async showQuickLookForPupil(event) {
    event.preventDefault();

    const sessionId = this.data.get("sessionId");
    const pupilId = this.data.get("pupilId");
    const attendanceId = this.data.get("attendanceId");

    const quickViewElement = document.querySelector("#school-dashboard-lesson-quick-look");
    quickViewElement.classList.remove("u-hidden");
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch(`/schools/classroom/sessions/${sessionId}/${attendanceId}/quick-look`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html",
        "body": JSON.stringify({ "pupil_id": pupilId, "attendance_id": attendanceId })
      }
    });

    if (response.ok) {

      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("school_lesson_quick_look");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  async showQuickLook(event) {
    event.preventDefault();

    const sessionId = this.data.get("sessionId");

    const quickViewElement = document.querySelector("#school-dashboard-lesson-quick-look");
    quickViewElement.classList.remove("u-hidden");
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch(`/schools/classroom/sessions/${sessionId}/quick-look/dashboard`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    });

    if (response.ok) {

      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("school_lesson_quick_look");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  async showEditQuickLook(event) {
    event.preventDefault();

    const programId = this.data.get("programId");

    const quickViewElement = document.querySelector("#school-classrooms-quick-look");

    quickViewElement.classList.remove("u-hidden");
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch(`/schools/classroom/program/${programId}/quick-look/edit`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    });

    if (response.ok) {
      const responseBody = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(responseBody, "text/html");
      const template = xmlDoc.querySelector("template");
      const targetElement = document.getElementById("school-classrooms-quick-look");

      if (template && targetElement) {
        targetElement.innerHTML = "";
        targetElement.appendChild(template.content.cloneNode(true));
      }
    }
  }

  toggleErrorPanel(show, errorMessage) {
    const errorSection = document.getElementById("error-section");
    const errorList = document.getElementById("errors-list");
  
    if (show) {
      errorSection.classList.remove("u-hidden");
      errorList.textContent = errorMessage || "An error occurred";
    } else {
      errorSection.classList.add("u-hidden");
      errorList.textContent = "";
    }
  }

  validateForm() {
    let validationErrors = [];

    const startTime = this.startTimeTarget?.value;

    if (!startTime) {
      validationErrors.push("Start date/time is required.");
    } else if (isNaN(Date.parse(startTime))) {
      validationErrors.push("Start date/time must be a valid date-time.");
    }

    const selectedDays = this.daysSessionsRunTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);

    if (selectedDays.length === 0) {
      validationErrors.push("At least one day must be selected for sessions.");
    }

    validationErrors.push(...this.validateCoursework());
    validationErrors.push(...this.validateNoBlankExistingCoursework());

    return validationErrors;
  }

  validateCoursework() {
    const errors = [];

    const addToFormContainers = this.element.querySelectorAll('[data-controller~="add-to-form"]');

    const addToFormContainer = addToFormContainers[0];
    if (!addToFormContainer) return [];

    const addToFormRows = addToFormContainer.querySelectorAll(
      '[data-add-to-form-target="container"] [data-add-to-form-target="row"]'
    );

    let total = 0;
    let anyCompletedItem = false;

    addToFormRows.forEach((row, index) => {
      const nameInput = row.querySelector('input[name$="[name]"]');
      const weightInput = row.querySelector('input[name$="[weight]"]');

      const nameValue = (nameInput?.value || "").trim();
      const weightValue = (weightInput?.value || "").trim();

      const namePresent = nameValue.length > 0;
      const weightPresent = weightValue.length > 0;

      if (!namePresent && !weightPresent) return;

      if (namePresent && !weightPresent) {
        errors.push(`Coursework item ${index + 1} must have a weight.`);
      }

      if (weightPresent && !namePresent) {
        errors.push(`Coursework item ${index + 1} must have a name.`);
      }

      const weight = Number(weightValue);
      if (Number.isNaN(weight)) {
        errors.push(`Coursework item ${index + 1} weight must be a number.`);
      }

      anyCompletedItem = true;
      total += weight;
    });

    if (!anyCompletedItem) return errors;

    const rounded = Math.round(total * 100) / 100;
    if (rounded !== 100) {
      errors.push(`Coursework weights must total 100% (currently ${rounded}%).`);
    }

    return errors;
  }

  validateNoBlankExistingCoursework() {
    const errors = [];

    const current = this.initialCourseworkById();

    this._initialCourseworkById.forEach((initialVals, id) => {
      const startedWithContent = (initialVals.name || "") !== "" || (initialVals.weight || "") !== "";
      if (!startedWithContent) return;

      const cur = current.get(id);
      if (!cur) return;

      const nowBlank = (cur.name || "") === "" && (cur.weight || "") === "";
      if (nowBlank) {
        errors.push(
          "You cleared an existing coursework item. Either re-enter its name and weight, or remove it using the X button."
        );
      }
    });

    return errors;
  }

  showValidationBlock(message) {
    this.validationMessageTarget.innerHTML = message;
    this.validationBlockTarget.hidden = false;
    this.validationMessageTarget.hidden = false;
  }

  hideValidationBlock() {
    if (!this.hasValidationBlockTarget) return;
    this.validationBlockTarget.hidden = true;
    if (this.hasValidationMessageTarget) this.validationMessageTarget.textContent = "";
  }

  showConfirmPanelForRow(row, controller, message) {
    this._courseworkRemoveRequest = { row, controller };

    if (this.hasConfirmMessageTarget) {
      this.confirmMessageTarget.textContent = message;
    }

    if (this.hasConfirmPanelTarget) {
      this.confirmPanelTarget.hidden = false;
      this.confirmPanelTarget.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  hideConfirmPanel() {
    if (this.hasConfirmPanelTarget) this.confirmPanelTarget.hidden = true;
    if (this.hasConfirmMessageTarget) this.confirmMessageTarget.textContent = "";
  }

  confirmCourseworkRemove(event) {
    event.preventDefault();
    if (!this._courseworkRemoveRequest) return;

    this.hideConfirmPanel();

    const { row, controller } = this._courseworkRemoveRequest;
    this._courseworkRemoveRequest = null;

    if (row && row.isConnected) row.remove();

    if (controller) {
      controller.ensureMinRows();
      controller.updateDeleteButtons();
    }
  }

  cancelCourseworkRemove(event) {
    event.preventDefault();
    this._courseworkRemoveRequest = null;
    this.hideConfirmPanel();
  }

}